import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { AnnouncementHistoryItem } from '~/shared/data-contract/announcements';

import { useActiveAnnouncement } from './api/use-active-announcement';
import { useAnnouncementDismissal } from './api/use-announcement-dismissal';
import { useAnnouncementHistory } from './api/use-announcement-history';
import { isKnownNotificationType } from './content';
import { WhatsNewModal } from './WhatsNewModal';

type AnnouncementsContextValue = {
    readonly history: readonly AnnouncementHistoryItem[] | undefined;
    readonly openModal: () => void;
};

const DISABLED_CONTEXT_VALUE: AnnouncementsContextValue = { history: undefined, openModal: noop };

const AnnouncementsContext = createContext<AnnouncementsContextValue | undefined>(undefined);
const ANNOUNCEMENT_PREVIEW_SEARCH_PARAM = 'announcement';
const LOCAL_PREVIEW_HOSTNAMES = new Set(['', 'localhost', '127.0.0.1', '::1']);

const isLocalAnnouncementPreviewHost = (): boolean => LOCAL_PREVIEW_HOSTNAMES.has(globalThis.location.hostname);

const getLocalPreviewAnnouncementType = (search: string): string | undefined => {
    if (!isLocalAnnouncementPreviewHost()) {
        return undefined;
    }

    const notificationType = new URLSearchParams(search).get(ANNOUNCEMENT_PREVIEW_SEARCH_PARAM);

    return is.nonEmptyString(notificationType) && isKnownNotificationType(notificationType)
        ? notificationType
        : undefined;
};

export const useAnnouncements = (): AnnouncementsContextValue => {
    const context = useContext(AnnouncementsContext);

    if (is.undefined(context)) {
        throw new Error('useAnnouncements must be used within AnnouncementsProvider');
    }

    return context;
};

type AnnouncementsProviderProps = {
    readonly children: ReactNode;
    readonly suppressed?: boolean;
};

export const AnnouncementsProvider: FunctionComponent<AnnouncementsProviderProps> = ({
    children,
    suppressed = false,
}) => {
    const { search } = useLocation();
    const { applicationState } = useApplicationContext();
    const previewNotificationType = useMemo(() => getLocalPreviewAnnouncementType(search), [search]);
    const isUserProfileComplete = applicationState.myUserProfile.mapOr(false, (profile) => profile.exists === true);
    const isEnabled =
        !is.undefined(previewNotificationType) ||
        (!suppressed &&
            isUserProfileComplete &&
            applicationState.featureFlagService.isFeatureFlagEnabled('announcements-enabled'));

    if (!isEnabled) {
        return <AnnouncementsContext.Provider value={DISABLED_CONTEXT_VALUE}>{children}</AnnouncementsContext.Provider>;
    }

    return (
        <AnnouncementsProviderContent previewNotificationType={previewNotificationType}>
            {children}
        </AnnouncementsProviderContent>
    );
};

const AnnouncementsProviderContent: FunctionComponent<{
    readonly children: ReactNode;
    readonly previewNotificationType: string | undefined;
}> = ({ children, previewNotificationType }) => {
    const { data: activeAnnouncements } = useActiveAnnouncement();
    const { data: history } = useAnnouncementHistory();
    const { mutate: dismiss } = useAnnouncementDismissal();

    const [modalOpen, setModalOpen] = useState(false);

    const renderableActiveAnnouncements = useMemo(
        () => activeAnnouncements?.filter((a) => isKnownNotificationType(a.notificationType)),
        [activeAnnouncements],
    );
    const renderableHistory = useMemo(
        () => history?.filter((item) => isKnownNotificationType(item.notificationType)),
        [history],
    );

    const activeAnnouncement = renderableActiveAnnouncements?.[0];
    const latestHistoryItem = renderableHistory?.[0];
    const modalNotificationType =
        previewNotificationType ?? activeAnnouncement?.notificationType ?? latestHistoryItem?.notificationType;

    const activeAnnouncementId = activeAnnouncement?.id;

    useEffect(() => {
        if (is.undefined(previewNotificationType) && is.undefined(activeAnnouncementId)) {
            return;
        }
        setModalOpen(true);
    }, [activeAnnouncementId, previewNotificationType]);

    const openModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    const handleDismiss = useCallback(() => {
        if (!is.undefined(previewNotificationType)) {
            return;
        }
        if (is.undefined(activeAnnouncementId)) {
            return;
        }
        dismiss(activeAnnouncementId);
    }, [activeAnnouncementId, dismiss, previewNotificationType]);

    const value = useMemo<AnnouncementsContextValue>(
        () => ({ history: renderableHistory, openModal }),
        [renderableHistory, openModal],
    );

    return (
        <AnnouncementsContext.Provider value={value}>
            {children}
            {!is.undefined(modalNotificationType) && (
                <WhatsNewModal
                    notificationType={modalNotificationType}
                    open={modalOpen}
                    onDismiss={handleDismiss}
                    onOpenChange={setModalOpen}
                />
            )}
        </AnnouncementsContext.Provider>
    );
};
